import React from 'react';
import { connect } from 'react-redux';
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import { Link } from 'react-router-dom';
import {
  ActivityFeed,
  Card,
  CardCol,
  CardRow,
  Image,
  EmptyMessage,
  ErrorMessage,
  LoadingMessage,
  PaginationBar,
  Utils,
  C,
  selectServicesKapp,
} from '@kineticdata/bundle-common';
import { I18n, defineKqlQuery, searchSubmissions } from '@kineticdata/react';
import { PageTitle } from '../shared/PageTitle';
import classNames from 'classnames';
import { Map } from 'immutable';

const ActivityFeedContent = props => (
  <>
    {!props.loading &&
      props.errors && (
        <div className="alert alert-danger alert-bar">
          {Map(props.errors)
            .map((error, key) => (
              <p key={key}>
                <div>
                  <I18n>{`Failed to retrieve ${key}.`}</I18n>
                </div>
                <small className="text-muted">{error.message}</small>
              </p>
            ))
            .toList()}
        </div>
      )}
    {props.data ? (
      <>
        {!props.loading &&
          !props.errors &&
          props.data.length === 0 && (
            <EmptyMessage
              title="There are no news articles."
              message="Check back here to stay up to date on the latest news."
            />
          )}
        {props.dataElements}
        {!props.hidePaging && (
          <PaginationBar
            className="mb-3"
            paging={props.loading}
            previousPage={
              props.hasPreviousPage ? props.getPreviousPage : undefined
            }
            nextPage={props.hasNextPage ? props.getNextPage : undefined}
            startIndex={props.startIndex}
            endIndex={props.endIndex}
            count={props.count}
            countDone={true}
            pageSize={25}
          />
        )}
      </>
    ) : props.loading ? (
      <LoadingMessage />
    ) : (
      <ErrorMessage message="An unexpected error ocurred." />
    )}
  </>
);

export const NewsComponent = props => (
  <div className="page-container">
    <div className="page-panel">
      <PageTitle parts={['Latest News']} title={'Latest News'}>
        <div className="ml-auto">
          <label htmlFor="archived-input">
            <input
              type="checkbox"
              id="archived-input"
              checked={props.archived}
              onChange={props.handleArchivedToggle}
              className="mr-2"
            />
            <I18n>Show Archived News</I18n>
          </label>
        </div>
      </PageTitle>

      <div className="cards">
        <ActivityFeed
          feedKey={props.feedKey}
          pageSize={25}
          joinByDirection="DESC"
          joinBy="createdAt"
          options={{
            archived: props.archived,
          }}
          dataSources={props.dataSources}
          showCount={true}
        >
          {ActivityFeedContent}
        </ActivityFeed>
      </div>
    </div>
  </div>
);

const NewsCard = ({ submission }) => {
  const expired =
    submission.values[C.FIELD_EXPIRATION_DATE] <
    new Date().toISOString().substring(0, 10);
  const showcase =
    !expired && submission.values['Showcase'].includes('Showcase');
  const backgroundImage =
    submission.values['Image'] ||
    Utils.getAttributeValue(submission.form || {}, 'Image');
  return (
    <Card
      className={classNames('card__news', { 'card__news--showcase': showcase })}
    >
      {backgroundImage && <Image src={backgroundImage} alt="" />}
      <CardCol middle>
        <CardRow type="title">
          <I18n>{submission.values['Title']}</I18n>
        </CardRow>
        <CardRow className="card__row--description">
          <I18n>{submission.values['Description']}</I18n>
        </CardRow>
        <CardRow>
          <strong>
            {submission.values['Link'] ? (
              <a
                href={submission.values['Link']}
                target="_blank"
                rel="noopener noreferrer"
              >
                <I18n>{submission.values['Link Label'] || 'Read More'}</I18n>
              </a>
            ) : submission.id ? (
              <Link to={`/news/${submission.id}`}>
                <I18n>{submission.values['Link Label'] || 'Read More'}</I18n>
              </Link>
            ) : null}
          </strong>
        </CardRow>
        <CardRow>
          {showcase && (
            <span className="badge badge-dark badge-pill">
              <I18n>Showcased</I18n>
            </span>
          )}
          {expired && (
            <span className="badge badge-dark badge-pill">
              <I18n>Archived</I18n>
            </span>
          )}
        </CardRow>
      </CardCol>
    </Card>
  );
};

const mapStateToProps = state => ({
  locales: state.app.locales,
  timezones: state.app.timezones,
  servicesKapp: selectServicesKapp(state),
});

const mapDispatchToProps = {};

const buildSearch = archived => {
  const searcher = defineKqlQuery()
    .in('coreState', 'coreState')
    .equals(`values[${C.FIELD_STATUS}]`, 'status');
  if (!archived) {
    searcher.greaterThanOrEquals(`values[${C.FIELD_EXPIRATION_DATE}]`, 'date');
  }

  return searcher.end()({
    coreState: [C.CORE_STATE_DRAFT, C.CORE_STATE_SUBMITTED],
    status: 'Active',
    date: new Date().toISOString().substring(0, 10),
  });
};

export const News = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withState('archived', 'setArchived', false),
  withHandlers({
    handleArchivedToggle: props => e => props.setArchived(e.target.checked),
    handleQueryTransform: props => submissions =>
      props.archived
        ? submissions
        : submissions.sort(
            (a, b) =>
              a.values['Showcase'][0] !== b.values['Showcase'][0]
                ? a.values['Showcase'][0] === 'Showcase'
                  ? -1
                  : 1
                : a.createdAt > b.createdAt
                  ? -1
                  : b.createdAt > a.createdAt
                    ? 1
                    : 0,
          ),
    handleNewsCardRender: props => record => (
      <NewsCard key={record.id} submission={record} />
    ),
  }),
  withProps(props => ({
    dataSources: {
      news: {
        fn: searchSubmissions,
        params: (prevParams, prevResult, options) =>
          prevParams && prevResult
            ? prevResult.nextPageToken
              ? { ...prevParams, pageToken: prevResult.nextPageToken }
              : null
            : {
                kapp: C.KAPP_SLUG_ADMIN,
                form: C.FORM_SLUG_NEWS,
                limit: props.archived ? 50 : 1000,
                search: {
                  q: buildSearch(props.archived),
                  orderBy: props.archived
                    ? 'createdAt'
                    : `values[${C.FIELD_EXPIRATION_DATE}],createdAt`,
                  direction: 'DESC',
                  include: ['details', 'values', 'form.attributes[Image]'],
                },
                count: true,
              },
        transform: result => ({
          data: props.handleQueryTransform(result.submissions),
          nextPageToken: result.nextPageToken,
        }),
        component: props.handleNewsCardRender,
      },
    },
  })),
  lifecycle({
    componentDidMount() {},
    componentDidUpdate(prevProps) {},
  }),
)(NewsComponent);
